<template>
  <div class="tender-detail">
    <!-- Loading State -->
    <div v-if="tenderStore.isLoading && !tenderStore.currentTender" class="loading-container">
      <q-spinner color="primary" size="3em" />
      <div class="q-mt-md">Loading tender details...</div>
    </div>
    
    <!-- Error State -->
    <div v-else-if="tenderStore.error && !tenderStore.currentTender" class="error-container">
      <q-icon name="error" size="4em" color="negative" />
      <div class="text-h6 q-mt-md">{{ tenderStore.error }}</div>
      <q-btn color="primary" label="Retry" class="q-mt-md" @click="loadTender" />
    </div>
    
    <!-- Tender Details -->
    <div v-else-if="tenderStore.currentTender" class="tender-content">
      <!-- Detail Page Header -->
      <DetailPageHeader
        :title="tenderStore.currentTender.name"
        :subtitle="tenderStore.currentTender.reference_number ? `Reference: ${tenderStore.currentTender.reference_number}` : 'Tender Details'"
        icon="gavel"
        back-route="/tenders"
        back-label="Back to Tenders"
        :status="{
          label: getStatusLabel(tenderStore.currentTender.status),
          color: getStatusColor(tenderStore.currentTender.status)
        }"
        :stats="headerStats"
        :actions="modifiedHeaderActions"
      />

      <!-- Content Area -->
      <div class="content-area">
        <!-- Tab Navigation -->
        <div class="tab-navigation">
          <q-tabs
            v-model="activeTab"
            class="tender-tabs"
            active-color="primary"
            indicator-color="primary"
            align="left"
            no-caps
          >
            <q-tab 
              name="overview" 
              icon="dashboard" 
              label="Overview"
              class="tab-item"
            />
            <q-tab 
              name="contractors" 
              icon="business" 
              label="Contractors"
              class="tab-item"
            >
              <q-badge 
                v-if="contractorCount > 0"
                color="primary" 
                floating
              >
                {{ contractorCount }}
              </q-badge>
            </q-tab>
            <q-tab 
              name="quotes" 
              icon="description" 
              label="Quotes"
              class="tab-item"
            >
              <q-badge 
                v-if="quoteCount > 0"
                color="secondary" 
                floating
              >
                {{ quoteCount }}
              </q-badge>
            </q-tab>
            <q-tab 
              name="communications" 
              icon="email" 
              label="Communications"
              class="tab-item"
            />
            <q-tab 
              name="documents" 
              icon="folder" 
              label="Documents"
              class="tab-item"
            />
            <q-tab 
              name="timeline" 
              icon="timeline" 
              label="Timeline"
              class="tab-item"
            />
            <q-tab
              name="followups"
              icon="contact_phone"
              label="Followups"
              class="tab-item"
            >
              <q-badge
                v-if="followupCount > 0"
                color="orange"
                floating
              >
                {{ followupCount }}
              </q-badge>
            </q-tab>
            <q-tab
              name="supply-rules"
              icon="rule"
              label="Supply Rules"
              class="tab-item"
            />
            <q-tab
              name="analytics"
              icon="analytics"
              label="Analytics"
              class="tab-item"
            />
            <q-tab
              name="letter-content"
              icon="description"
              label="Letter Content"
              class="tab-item"
            />
            <q-tab
              name="letter"
              icon="mail"
              label="Tender Letter"
              class="tab-item"
            />
          </q-tabs>
        </div>

        <!-- Tab Content -->
        <div class="tab-content">
          <q-tab-panels 
            v-model="activeTab" 
            animated 
            transition-prev="slide-right"
            transition-next="slide-left"
            class="full-height"
          >
            <!-- Overview Tab -->
            <q-tab-panel name="overview" class="q-pa-lg">
              <TenderOverviewPanel
                :tender="tenderStore.currentTender"
                @edit-tender="editTender"
                @add-contractor="showContractorDialog = true"
                @add-quote="showQuoteDialog = true"
              />
            </q-tab-panel>

            <!-- Contractors Tab -->
            <q-tab-panel name="contractors" class="q-pa-lg">
              <TenderContractorsPanel
                :tender-id="tenderId"
                :contractors="tenderStore.currentTender.contractors || []"
                @add-contractor="showContractorDialog = true"
                @edit-contractor="editContractor"
                @delete-contractor="confirmDeleteContractor"
                @send-email="sendEmailToContractor"
                @upload-document="uploadDocument"
              />
            </q-tab-panel>

            <!-- Quotes Tab -->
            <q-tab-panel name="quotes" class="q-pa-lg">
              <TenderQuotesPanel
                :tender-id="tenderId"
                :quotes="tenderStore.currentTender.latest_quotes || []"
                @add-quote="showQuoteDialog = true"
                @edit-quote="editQuote"
                @delete-quote="confirmDeleteQuote"
              />
            </q-tab-panel>

            <!-- Communications Tab -->
            <q-tab-panel name="communications" class="q-pa-lg">
              <TenderCommunicationsPanel
                :tender-id="tenderId"
                :tender="tenderStore.currentTender"
                @send-email="sendEmailToContractor"
                @add-comment="addComment"
              />
            </q-tab-panel>

            <!-- Documents Tab -->
            <q-tab-panel name="documents" class="q-pa-lg">
              <TenderDocumentsPanel
                :tender-id="tenderId"
                :documents="tenderStore.currentTender.documents || []"
                @upload-document="uploadDocument"
                @delete-document="deleteDocument"
              />
            </q-tab-panel>

            <!-- Timeline Tab -->
            <q-tab-panel name="timeline" class="q-pa-lg">
              <TenderTimelinePanel
                :tender-id="tenderId"
                :activities="tenderStore.currentTender.activities || []"
                :comments="tenderStore.currentTender.comments || []"
              />
            </q-tab-panel>

            <!-- Followups Tab -->
            <q-tab-panel name="followups" class="q-pa-lg">
              <TenderFollowups
                :tender-id="tenderId"
                :contractors="tenderStore.currentTender.contractors || []"
                @updated="onFollowupsUpdated"
              />
            </q-tab-panel>

            <!-- Supply Rules Tab -->
            <q-tab-panel name="supply-rules" class="q-pa-lg">
              <TenderSupplyRules
                :tender-id="tenderId"
              />
            </q-tab-panel>

            <!-- Analytics Tab -->
            <q-tab-panel name="analytics" class="q-pa-lg">
              <TenderAnalyticsPanel
                :tender="tenderStore.currentTender"
                :contractors="tenderStore.currentTender.contractors || []"
                :quotes="tenderStore.currentTender.latest_quotes || []"
              />
            </q-tab-panel>

            <!-- Letter Content Tab -->
            <q-tab-panel name="letter-content" class="q-pa-lg">
              <TenderLetterContentPanel
                :tender-id="tenderId"
              />
            </q-tab-panel>

            <!-- Tender Letter Tab -->
            <q-tab-panel name="letter" class="q-pa-lg">
              <TenderLetterHistoryPanel
                ref="historyPanel"
                :tender-id="parseInt(tenderId)"
              />
            </q-tab-panel>
          </q-tab-panels>
        </div>
      </div>
    </div>
    
    <!-- Not Found State -->
    <div v-else class="not-found-container">
      <q-icon name="search_off" size="4em" color="grey-6" />
      <div class="text-h6 q-mt-md">Tender not found</div>
      <div class="text-body2 q-mt-sm text-grey-7">
        The requested tender could not be found or you don't have permission to view it.
      </div>
      <q-btn color="primary" label="Back to Tenders" class="q-mt-lg" @click="router.push('/tenders')" />
    </div>

    <!-- Enhanced Tender Form Dialog -->
    <TenderForm
      v-model="showTenderDialog"
      :tender="tenderStore.currentTender"
      @save="onTenderSaved"
      @cancel="showTenderDialog = false"
    />

    <!-- Enhanced Dialogs -->
    <TenderContractorDialog
      v-model="showContractorDialog"
      :tender-id="tenderId"
      :contractor="selectedContractor"
      :existing-organization-ids="existingContractorOrgIds"
      @save="onContractorSaved"
      @cancel="onContractorCancelled"
    />

    <TenderQuoteDialog
      v-model="showQuoteDialog"
      :tender-id="tenderId"
      :quote="selectedQuote"
      :tender="tender"
      @save="onQuoteSaved"
      @cancel="onQuoteCancelled"
    />

    <!-- Tender Letter Generator Dialog -->
    <TenderLetterGeneratorDialog
      v-model="showLetterGeneratorDialog"
      :tender-id="tenderId"
      @letter-generated="onLetterGenerated"
    />

    <!-- Delete Confirmation Dialog -->
    <q-dialog v-model="showDeleteDialog" persistent>
      <q-card>
        <q-card-section class="row items-center">
          <q-avatar icon="warning" color="negative" text-color="white" />
          <span class="q-ml-sm text-h6">Confirm Deletion</span>
        </q-card-section>

        <q-card-section>
          <p>Are you sure you want to delete this tender?</p>
          <p class="text-caption text-grey-7">
            This will permanently delete the tender and all associated data. This action cannot be undone.
          </p>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn v-close-popup flat label="Cancel" />
          <q-btn
            label="Delete"
            color="negative"
            :loading="tenderStore.isLoading"
            @click="confirmDeleteTender"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Convert to Project Confirmation Dialog -->
    <q-dialog v-model="showConvertToProjectDialog" persistent>
      <q-card style="min-width: 400px">
        <q-card-section class="row items-center">
          <q-avatar icon="rocket_launch" color="positive" text-color="white" />
          <span class="q-ml-sm text-h6">Convert to Project</span>
        </q-card-section>

        <q-card-section>
          <p class="text-body1">
            Convert this won tender to a new project?
          </p>
          <p class="text-body2 q-mt-sm">
            <strong>Tender:</strong> {{ tenderStore.currentTender?.name }}
          </p>
          <p class="text-caption text-grey-7 q-mt-sm">
            This will create a new project based on this tender's information including:
          </p>
          <ul class="text-caption text-grey-7 q-mt-xs">
            <li>Customer and site details</li>
            <li>Contract value from winning quote</li>
            <li>Timeline from tender dates</li>
            <li>Link back to this tender</li>
          </ul>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn v-close-popup flat label="Cancel" color="grey-7" />
          <q-btn
            label="Create Project"
            color="positive"
            icon="rocket_launch"
            :loading="isConverting"
            @click="convertToProject"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { useTenderStore } from '../../stores/tenders';
  import { useProjectStore } from '../../stores/projects';
  import { useQuasar } from 'quasar';

  // Components
  import DetailPageHeader from '../common/DetailPageHeader.vue';
  import TenderForm from './TenderForm.vue';
  import TenderOverviewPanel from './detail-panels/TenderOverviewPanel.vue';
  import TenderContractorsPanel from './detail-panels/TenderContractorsPanel.vue';
  import TenderQuotesPanel from './detail-panels/TenderQuotesPanel.vue';
  import TenderCommunicationsPanel from './detail-panels/TenderCommunicationsPanel.vue';
  import TenderDocumentsPanel from './detail-panels/TenderDocumentsPanel.vue';
  import TenderTimelinePanel from './detail-panels/TenderTimelinePanel.vue';
  import TenderAnalyticsPanel from './detail-panels/TenderAnalyticsPanel.vue';
  import TenderContractorDialog from './dialogs/TenderContractorDialog.vue';
  import TenderQuoteDialog from './dialogs/TenderQuoteDialog.vue';
  import TenderLetterContentPanel from './letter-content/TenderLetterContentPanel.vue';
  import TenderLetterGeneratorDialog from './letter/TenderLetterGeneratorDialog.vue';
  import TenderLetterHistoryPanel from './letter/TenderLetterHistoryPanel.vue';
  import TenderFollowups from './TenderFollowups.vue';
  import TenderSupplyRules from './TenderSupplyRules.vue';

  const route = useRoute();
  const router = useRouter();
  const tenderStore = useTenderStore();
  const projectStore = useProjectStore();
  const $q = useQuasar();

  // State
  const activeTab = ref('overview');
  const showConvertToProjectDialog = ref(false);
  const isConverting = ref(false);
  const showTenderDialog = ref(false);
  const showContractorDialog = ref(false);
  const showQuoteDialog = ref(false);
  const showDeleteDialog = ref(false);
  const showLetterGeneratorDialog = ref(false);
  const selectedContractor = ref(null);
  const selectedQuote = ref(null);
  const historyPanel = ref(null);

  // Computed properties
  const tenderId = computed(() => {
    return Number(route.params.id);
  });

  const tender = computed(() => {
    return tenderStore.currentTender;
  });

  const contractorCount = computed(() => {
    return tenderStore.currentTender?.contractors?.length || 0;
  });

  const existingContractorOrgIds = computed(() => {
    return (tenderStore.currentTender?.contractors || [])
      .map((c) => c.organization_id)
      .filter(Boolean);
  });

  const quoteCount = computed(() => {
    return tenderStore.currentTender?.latest_quotes?.length || 0;
  });

  const followupCount = computed(() => {
    return tenderStore.currentTender?.followups?.length || 0;
  });

  const formatTotalValue = computed(() => {
    const quotes = tenderStore.currentTender?.latest_quotes || [];
    if (quotes.length === 0) {return '$0';}
    const total = quotes.reduce((sum, q) => sum + (parseFloat(q.total_amount) || 0), 0);
    return '$' + total.toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  });

  const headerStats = computed(() => [
    { value: tenderStore.currentTender?.site_name || 'No site', label: 'Site', icon: 'location_on' },
    { value: contractorCount.value, label: 'Contractors', icon: 'people' },
    { value: quoteCount.value, label: 'Quotes', icon: 'description' },
    { value: formatTotalValue.value, label: 'Value', icon: 'payments' },
  ]);

  // Check if tender can be converted to project
  const canConvertToProject = computed(() => {
    const tender = tenderStore.currentTender;
    return tender && tender.status === 'won' && !tender.project_id;
  });

  const modifiedHeaderActions = computed(() => {
    const actions = [];

    // Add Convert to Project button if tender is won and not already converted
    if (canConvertToProject.value) {
      actions.push({
        icon: 'rocket_launch',
        label: 'Convert to Project',
        color: 'positive',
        textColor: 'white',
        onClick: () => { showConvertToProjectDialog.value = true; },
      });
    }

    // Add Generate Letter button
    actions.push({
      icon: 'description',
      label: 'Generate Letter',
      color: 'white',
      textColor: 'primary',
      onClick: () => { showLetterGeneratorDialog.value = true; },
    });

    // Add more actions menu
    actions.push({
      icon: 'more_vert',
      flat: true,
      round: true,
      color: 'white',
      textColor: 'white',
      menu: [
        { icon: 'edit', label: 'Edit Tender', onClick: editTender },
        { icon: 'content_copy', label: 'Duplicate Tender', onClick: duplicateTender },
        { icon: 'archive', label: 'Archive', onClick: archiveTender },
        { separator: true },
        { icon: 'delete', label: 'Delete Tender', onClick: deleteTender },
      ],
    });

    return actions;
  });

  // Methods
  async function loadTender() {
    if (!tenderId.value) {return;}
  
    try {
      // Use regular tender API with comprehensive includes to get everything in one call
      await tenderStore.fetchTender(tenderId.value, [
        'contractors', 
        'contractors.contacts', 
        'quotes', 
        'site', 
        'comments', 
        'activities',
      ]);
    } catch (error) {
      $q.notify({
        color: 'negative',
        message: `Failed to load tender: ${error.message}`,
        icon: 'warning',
      });
    }
  }

  function editTender() {
    showTenderDialog.value = true;
  }

  function duplicateTender() {
    $q.notify({
      color: 'info',
      message: 'Tender duplication will be available in a future update',
      timeout: 3000,
    });
  }

  function archiveTender() {
    $q.notify({
      color: 'info',
      message: 'Tender archiving will be available in a future update',
      timeout: 3000,
    });
  }

  async function convertToProject() {
    isConverting.value = true;
    try {
      const response = await projectStore.convertTenderToProject(tenderId.value, {});
      // API returns { message, project } - extract the project
      const newProject = response.project || response;

      showConvertToProjectDialog.value = false;

      $q.notify({
        type: 'positive',
        message: 'Project created successfully! Redirecting to project...',
        timeout: 3000,
      });

      // Navigate to the new project detail page
      router.push({ name: 'ProjectDetail', params: { id: newProject.id } });
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to create project: ' + (error.message || 'Unknown error'),
        timeout: 5000,
      });
    } finally {
      isConverting.value = false;
    }
  }

  function deleteTender() {
    showDeleteDialog.value = true;
  }

  async function confirmDeleteTender() {
    try {
      await tenderStore.deleteTender(tenderId.value);
      showDeleteDialog.value = false;
    
      $q.notify({
        color: 'positive',
        message: 'Tender deleted successfully',
        timeout: 3000,
      });
    
      router.push('/tenders');
    } catch (error) {
      $q.notify({
        color: 'negative',
        message: `Failed to delete tender: ${error.message}`,
        timeout: 4000,
      });
    }
  }

  async function onTenderSaved(tender) {
    showTenderDialog.value = false;
    await loadTender();
  
    $q.notify({
      color: 'positive',
      message: 'Tender updated successfully',
      timeout: 3000,
    });
  }

  function editContractor(contractor) {
    selectedContractor.value = contractor;
    showContractorDialog.value = true;
  }

  async function confirmDeleteContractor(contractor) {
    try {
      await tenderStore.removeContractor(tenderId.value, contractor.id);
      $q.notify({
        color: 'positive',
        message: `${contractor.business_name} removed successfully`,
        timeout: 3000,
      });
      loadTender();
    } catch (error) {
      $q.notify({
        color: 'negative',
        message: 'Failed to remove contractor: ' + error.message,
        timeout: 5000,
      });
    }
  }

  function sendEmailToContractor(contractor) {
    $q.notify({
      color: 'info',
      message: 'Opening email client for ' + contractor.business_name,
      timeout: 3000,
    });
  
    // Open default email client with tender information
    const subject = encodeURIComponent(`Tender: ${tenderStore.currentTender?.name || 'Invitation'}`);
    const body = encodeURIComponent(`Dear ${contractor.contact?.name || 'Sir/Madam'},\n\nYou are invited to submit a quote for the following tender:\n\nTender: ${tenderStore.currentTender?.name}\nSite: ${tenderStore.currentTender?.site?.name}\n\nPlease contact us for further details.\n\nRegards`);
    const email = contractor.contact?.email || contractor.email;
  
    if (email) {
      window.open(`mailto:${email}?subject=${subject}&body=${body}`);
    } else {
      $q.notify({
        color: 'warning',
        message: 'No email address found for this contractor',
        timeout: 3000,
      });
    }
  }

  function uploadDocument(contractor) {
    // Create file input element
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.doc,.docx,.xls,.xlsx,.txt';
    input.multiple = false;
  
    input.onchange = async (event) => {
      const file = event.target.files[0];
      if (!file) {return;}
    
      try {
        await tenderStore.uploadContractorDocument(tenderId.value, contractor.id, file);
        $q.notify({
          color: 'positive',
          message: 'Document uploaded successfully',
          timeout: 3000,
        });
        loadTender();
      } catch (error) {
        $q.notify({
          color: 'negative',
          message: 'Failed to upload document: ' + error.message,
          timeout: 5000,
        });
      }
    };
  
    input.click();
  }

  async function onContractorSaved(contractorData) {
    try {
      // The TenderContractorResource flattens the structure, so pivot data is at the root level
      // When editing: selectedContractor.id is the tender_contractors.id (pivot ID)
      // When editing: selectedContractor.contractor_id exists
      // When adding new: selectedContractor is null
      const isEditing = selectedContractor.value && selectedContractor.value.organization_id;

      if (isEditing) {
        // Update existing contractor using the tender_contractors table ID
        await tenderStore.updateContractor(tenderId.value, selectedContractor.value.id, contractorData);

        $q.notify({
          color: 'positive',
          message: 'Contractor updated successfully',
          timeout: 3000,
        });
      } else {
        // Add new contractor
        await tenderStore.addContractor(tenderId.value, contractorData);

        $q.notify({
          color: 'positive',
          message: 'Contractor added successfully',
          timeout: 3000,
        });
      }

      showContractorDialog.value = false;
      selectedContractor.value = null;
      loadTender();
    } catch (error) {
      const action = selectedContractor.value && selectedContractor.value.organization_id ? 'update' : 'add';
      $q.notify({
        color: 'negative',
        message: `Failed to ${action} contractor: ${error.message}`,
        timeout: 5000,
      });
    }
  }

  function onContractorCancelled() {
    showContractorDialog.value = false;
    selectedContractor.value = null;
  }

  function editQuote(quote) {
    selectedQuote.value = quote;
    showQuoteDialog.value = true;
  }

  function confirmDeleteQuote(quote) {
    $q.dialog({
      title: 'Remove Quote',
      message: 'Are you sure you want to remove this quote from the tender?',
      cancel: true,
      persistent: true,
    }).onOk(async () => {
      try {
        await tenderStore.removeQuote(tenderId.value, quote.id);
        $q.notify({
          color: 'positive',
          message: 'Quote removed successfully',
          timeout: 3000,
        });
        loadTender();
      } catch (error) {
        $q.notify({
          color: 'negative',
          message: 'Failed to remove quote: ' + error.message,
          timeout: 5000,
        });
      }
    });
  }



  async function onQuoteSaved(quoteData) {
    try {
      // Check if we're editing or creating
      const isEditing = selectedQuote.value && selectedQuote.value.id;

      if (isEditing) {
        // Update existing quote (if needed in future)
        await tenderStore.updateQuote(tenderId.value, selectedQuote.value.id, quoteData);

        $q.notify({
          color: 'positive',
          message: 'Quote updated successfully',
          timeout: 3000,
        });

        showQuoteDialog.value = false;
        selectedQuote.value = null;
        loadTender();
      } else {
        // Create new quote for tender using the Phase 3 containment architecture
        const createdQuote = await tenderStore.createQuoteForTender(tenderId.value, quoteData);

        $q.notify({
          color: 'positive',
          message: 'Quote created successfully. Redirecting to quote details...',
          timeout: 3000,
        });

        // Close dialog first
        showQuoteDialog.value = false;
        selectedQuote.value = null;

        // Navigate to the newly created quote's detail page
        // The API returns the full quote object in the data field
        if (createdQuote && createdQuote.id) {
          router.push(`/quotes/${createdQuote.id}`);
        } else {
          // Fallback: reload the tender if we don't get a valid quote ID
          loadTender();
        }
      }
    } catch (error) {
      $q.notify({
        color: 'negative',
        message: `Failed to ${selectedQuote.value ? 'update' : 'create'} quote: ${error.message}`,
        timeout: 5000,
      });
    }
  }

  function onQuoteCancelled() {
    showQuoteDialog.value = false;
    selectedQuote.value = null;
  }

  function addComment(comment) {
    $q.dialog({
      title: 'Add Comment',
      prompt: {
        model: '',
        type: 'textarea',
        placeholder: 'Enter your comment here...',
      },
      cancel: true,
      persistent: true,
    }).onOk(async (commentText) => {
      if (!commentText.trim()) {return;}
    
      try {
        await tenderStore.addComment(tenderId.value, { comment: commentText });
        $q.notify({
          color: 'positive',
          message: 'Comment added successfully',
          timeout: 3000,
        });
        loadTender();
      } catch (error) {
        $q.notify({
          color: 'negative',
          message: 'Failed to add comment: ' + error.message,
          timeout: 5000,
        });
      }
    });
  }

  function deleteDocument(document) {
    $q.notify({
      color: 'info',
      message: 'Document deletion will be implemented',
      timeout: 3000,
    });
  }

  function onLetterGenerated() {
    // Refresh the history panel
    if (historyPanel.value) {
      historyPanel.value.loadHistory();
    }
  }

  function onFollowupsUpdated() {
    // Reload tender to update followup count
    loadTender();
  }

  // Utility functions
  function getStatusColor(status) {
    const colors = {
      pending: 'orange',
      submitted: 'blue',
      won: 'positive',
      lost: 'negative',
      draft: 'grey',
    };
    return colors[status] || 'grey';
  }

  function getStatusIcon(status) {
    const icons = {
      pending: 'schedule',
      submitted: 'send',
      won: 'emoji_events',
      lost: 'close',
      draft: 'edit',
    };
    return icons[status] || 'help';
  }

  function getStatusLabel(status) {
    const labels = {
      pending: 'Pending',
      submitted: 'Submitted',
      won: 'Won',
      lost: 'Lost',
      draft: 'Draft',
    };
    return labels[status] || status;
  }

  // Lifecycle
  onMounted(async () => {
    // Only load tender data - other stores will load their data as needed
    // Don't trigger redundant API calls for stores that are already loaded
    await loadTender();
  });

  // Watch for route changes
  watch(() => route.params.id, (newId) => {
    if (newId && newId !== tenderId.value) {
      loadTender();
    }
  });
</script>

<style lang="scss" scoped>
.tender-detail {
  height: 100vh;
  display: flex;
  flex-direction: column;

  .loading-container,
  .error-container,
  .not-found-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 50vh;
    text-align: center;
    color: #666;
  }

  .tender-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;


    .content-area {
      flex: 1;
      display: flex;
      flex-direction: column;
      background: #f8f9fa;
      overflow: hidden;

      .tab-navigation {
        flex-shrink: 0;
        background: white;
        border-bottom: 1px solid #e0e0e0;

        .tender-tabs {
          .tab-item {
            font-weight: 600;
            min-width: 100px;

            &.q-tab--active {
              background: rgba(25, 118, 210, 0.08);
            }
          }
        }
      }

      .tab-content {
        flex: 1;
        background: #f8f9fa;
        overflow: auto;

        .q-tab-panels {
          background: transparent;
          height: 100%;
        }
      }
    }
  }
}
</style>